import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { trackLandingCta } from "../../hooks/useLandingAnalytics";

/** Replaces the old three-tier pricing section while the product is in early
 * access: one centered card, everything free, no future prices anywhere.
 * Keeps id="pricing" so existing anchors keep working. Renders statically —
 * the old scroll-triggered reveal held the card invisible for seconds on
 * iPhone Safari (see SESSIONS 2026-06-12). */
export function EarlyAccess() {
  const { t } = useTranslation();
  const features = t("landing.earlyAccess.features", { returnObjects: true }) as string[];

  return (
    <section id="pricing" className="relative scroll-mt-16 overflow-hidden border-t border-slate-200/80 bg-white py-20 dark:border-white/5 dark:bg-slate-950 sm:py-28">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full glow [--glow:color-mix(in_srgb,var(--accent-400)_10%,transparent)] dark:[--glow:color-mix(in_srgb,var(--accent-600)_10%,transparent)]" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag={t("landing.earlyAccess.tag")}
          title={t("landing.earlyAccess.title")}
          subtitle={t("landing.earlyAccess.subtitle")}
        />

        <div className="relative mx-auto max-w-xl rounded-3xl bg-gradient-to-br from-blue-500/60 via-indigo-500/30 to-violet-500/50 p-px shadow-2xl shadow-blue-500/10">
          <div className="rounded-[calc(1.5rem-1px)] bg-white p-7 dark:bg-slate-900/95 sm:p-9">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 px-3 py-1 text-[11px] font-bold text-white shadow-lg shadow-blue-500/30 whitespace-nowrap">
            <Sparkles size={11} />
            {t("landing.earlyAccess.badge")}
          </span>

          <div className="text-center">
            <div className="flex items-end justify-center gap-2">
              <span className="text-5xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-6xl">
                {t("landing.earlyAccess.price")}
              </span>
            </div>
            <p className="mt-1 text-sm font-medium text-blue-700 dark:text-blue-200">
              {t("landing.earlyAccess.priceNote")}
            </p>
          </div>

          <ul className="mt-7 grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-100">
                <Check size={15} className="mt-0.5 flex-none text-emerald-600 dark:text-emerald-400" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/register?src=landing"
            onClick={() => trackLandingCta("early_access_card", "pricing")}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 text-white font-semibold px-5 py-3 text-sm shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5"
          >
            {t("landing.earlyAccess.cta")}
            <ArrowRight size={15} />
          </Link>

          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
            {t("landing.earlyAccess.finePrint")}
          </p>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-slate-500 max-w-md mx-auto">
          {t("landing.earlyAccess.afterNote")}
        </p>
      </div>
    </section>
  );
}
